/**
 * CollectionFilters
 * Tag-based filtering via URL params and sort-by controls.
 * Beer collections paginate client-side (all products rendered once in global sort order).
 */

class CollectionFilters {
  constructor() {
    this.section = document.querySelector('[data-collection-section]');
    this.grid = document.querySelector('[data-collection-grid]');
    this.skeleton = document.querySelector('[data-collection-skeleton]');
    this.results = document.querySelector('[data-collection-results]');
    this.filterBtns = document.querySelectorAll('[data-filter-btn]');
    this.sortSelect = document.querySelector('[data-sort-select]');
    this.defaultSort = this.sortSelect?.dataset.defaultSort || 'manual';
    this.clientPagination = this.section?.hasAttribute('data-client-pagination') ?? false;

    if (!this.section) return;

    if (this.clientPagination) {
      this.totalPages = parseInt(this.results?.dataset.totalPages || '1', 10);
      this.pageItems = this.section.querySelectorAll('[data-collection-page-item]');
    }

    this._bindEvents();
    this._setActiveFromUrl();

    if (this.clientPagination) {
      this._initClientPagination();
    }
  }

  _bindEvents() {
    this.filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-active')) return;
        const tag = btn.dataset.filterBtn;
        this._showLoadingState();
        this._applyFilter(tag);
      });
    });

    this.sortSelect?.addEventListener('change', () => {
      this._showLoadingState();
      this._applySort(this.sortSelect.value);
    });

    this.section.addEventListener('click', (event) => {
      const link = event.target.closest('.pagination__link[href]');
      if (!link || link.getAttribute('aria-current') === 'page') return;

      if (this.clientPagination) {
        event.preventDefault();
        const page = this._pageFromPaginationLink(link);
        if (page) this._showClientPage(page);
        return;
      }

      this._showLoadingState();
    });
  }

  _initClientPagination() {
    const page = this._getUrlPage();
    this._showClientPage(page, { updateUrl: false });

    if (!window.__collectionPopstateBound) {
      window.addEventListener('popstate', () => {
        const instance = document.querySelector('[data-client-pagination]');
        if (!instance) return;
        const filters = window.__collectionFiltersInstance;
        if (filters?.clientPagination) {
          filters._showClientPage(filters._getUrlPage(), { updateUrl: false });
        }
      });
      window.__collectionPopstateBound = true;
    }

    window.__collectionFiltersInstance = this;
  }

  _getUrlPage() {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('p') || '1', 10);
    if (!Number.isFinite(page) || page < 1) return 1;
    return Math.min(page, this.totalPages);
  }

  _pageFromPaginationLink(link) {
    if (link.dataset.page) {
      return parseInt(link.dataset.page, 10);
    }

    const url = new URL(link.href, window.location.origin);
    return parseInt(url.searchParams.get('p') || '1', 10);
  }

  _showClientPage(page, { updateUrl = true } = {}) {
    const targetPage = Math.min(Math.max(page, 1), this.totalPages);

    this.pageItems.forEach(item => {
      const itemPage = parseInt(item.dataset.page, 10);
      item.hidden = itemPage !== targetPage;
    });

    this._updatePaginationUI(targetPage);

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (targetPage === 1) {
        url.searchParams.delete('p');
      } else {
        url.searchParams.set('p', String(targetPage));
      }
      url.searchParams.delete('page');
      window.history.pushState({ collectionPage: targetPage }, '', url.toString());
    }

    this.results?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _updatePaginationUI(page) {
    const nav = this.section.querySelector('[data-pagination-client]');
    if (!nav) return;

    nav.querySelectorAll('.pagination__link[data-page]').forEach(el => {
      const elPage = parseInt(el.dataset.page, 10);
      const isNumberLink = !el.classList.contains('pagination__link--prev')
        && !el.classList.contains('pagination__link--next');

      if (!isNumberLink) return;

      const isCurrent = elPage === page;
      el.classList.toggle('pagination__link--current', isCurrent);

      if (isCurrent) {
        el.setAttribute('aria-current', 'page');
      } else {
        el.removeAttribute('aria-current');
      }
    });
  }

  _showLoadingState() {
    if (!this.grid || !this.skeleton) return;

    this.section.classList.add('is-loading');
    this.grid.hidden = true;
    this.skeleton.hidden = false;
    this.skeleton.setAttribute('aria-hidden', 'false');
    this.results?.setAttribute('aria-busy', 'true');

    this.filterBtns.forEach(btn => {
      btn.disabled = true;
    });
    if (this.sortSelect) {
      this.sortSelect.disabled = true;
    }
  }

  _displaySortValue(sort) {
    if (sort === 'most-relevant') return 'manual';
    return sort;
  }

  _isDefaultSort(sortValue) {
    return sortValue === this.defaultSort;
  }

  _setActiveFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const sort = params.get('sort_by') || this.defaultSort;
    const tag = this._getActiveTag();

    if (this.sortSelect) {
      const displaySort = this._displaySortValue(sort);
      const option = this.sortSelect.querySelector(`option[value="${displaySort}"]`);
      if (option) {
        this.sortSelect.value = displaySort;
      }
    }

    this.filterBtns.forEach(btn => {
      const btnTag = (btn.dataset.filterBtn || '').toLowerCase();
      const tagLower = tag.toLowerCase();
      btn.classList.toggle(
        'is-active',
        btnTag === tagLower || (!tag && btn.dataset.filterBtn === '')
      );
    });
  }

  _getActiveTag() {
    const path = window.location.pathname;
    const match = path.match(/\/collections\/[^/]+\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  _applyFilter(tag) {
    const url = new URL(window.location.href);
    const basePath = url.pathname.split('/').slice(0, 3).join('/');
    const newPath = tag ? `${basePath}/${encodeURIComponent(tag)}` : basePath;
    url.pathname = newPath;
    url.searchParams.delete('p');
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  _applySort(sortValue) {
    const url = new URL(window.location.href);
    if (sortValue && !this._isDefaultSort(sortValue)) {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }
    url.searchParams.delete('p');
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }
}

document.addEventListener('DOMContentLoaded', () => new CollectionFilters());
